export const useCartStore = defineStore('cart', () => {
  const { $axios } = useNuxtApp()
  const progressbarStore = useProgressbarStore()
  const { showSnackbar } = useSnackbarStore()
  const appConfigStore = useAppConfigStore()
  const appConfig = appConfigStore.appConfig

  const isLoading = ref(false)
  const cartData = ref(null)
  const cartItemCount = ref(0)

  // >> computed
  const cartItemTotal = computed(() => cartItemListWithTotals.value?.reduce((sum, item) => sum + item.subtotal, 0))
  const cartItemListWithTotals = computed(() => {
    return g.deepCopy(cartData.value?.cartItemList)?.map((item) => {
      _setItemDiscountPrice(item)
      const styleColorKey = p.getStyleColorKey(item)
      const totalQty = g.sumArray(item.qtyList)
      const subtotal = totalQty * p.getRawFinalPrice(item)
      const isActive = item.status == 'ACTIVE'
      return {
        ...item,
        isActive,
        styleColorKey,
        totalQty,
        subtotal
      }
    })
  })
  const offPriceStatus = computed(() => {
    let totalOffPriceQty = 0
    let targetMinQty = 0
    cartData.value?.cartItemList?.forEach((item) => {
      const isOffPriceItem = !!item.discountPrice && !!item.minDiscountQty
      if (isOffPriceItem) {
        const qtyTotal = item.qtyList.reduce((sum, qty) => sum + qty, 0)
        totalOffPriceQty += qtyTotal
        targetMinQty = item.minDiscountQty
      }
    })
    const isQualified = targetMinQty > 0 && totalOffPriceQty >= targetMinQty
    const remainingQty = isQualified ? 0 : targetMinQty - totalOffPriceQty
    return {
      isQualified,
      remainingQty,
      minQty: targetMinQty,
      totalOffPriceQty
    }
  })
  const _setItemDiscountPrice = (item) => {
    const itemCollectionList = item.promoCollectionList || []
    const hasPromoCollection = itemCollectionList.length > 0

    if (!hasPromoCollection) {
      if (!offPriceStatus.value.isQualified) return

      if (item.discountPrice && item.minDiscountQty) {
        item.price5Org = item.price5
        item.price5 = item.discountPrice
        item.isDiscountPriceApplied = true
      } else {
        item.price5 = item.price5Org || item.price5
        item.isDiscountPriceApplied = false
      }
      return
    }

    const isPromoRuleEnabled = !!cartData.value?.promoRuleSetting
    if (!isPromoRuleEnabled) return

    const promoRules = cartData.value?.promoRuleSetting?.promoByCollectionList || []
    if (!promoRules.length) return

    // Step 1: Find which promoGroups this item belongs to
    const itemGroups = new Set()
    promoRules.forEach((rule) => {
      if (rule.collectionList.some((cat) => itemCollectionList.some((ic) => ic.toLowerCase() === cat.toLowerCase()))) {
        itemGroups.add(rule.promoGroup)
      }
    })
    if (!itemGroups.size) return
    //console.log(`Item ${item.style} belongs to promo groups:`, Array.from(itemGroups))

    // Step 2: Sum total qty per promoGroup across all cart items
    const groupQtyMap = {}
    cartData.value?.cartItemList?.forEach((cartItem) => {
      const cartItemCollectionList = cartItem.promoCollectionList || []
      const cartItemGroups = new Set()
      promoRules.forEach((rule) => {
        if (
          rule.collectionList.some((cat) => cartItemCollectionList.some((ic) => ic.toLowerCase() === cat.toLowerCase()))
        ) {
          cartItemGroups.add(rule.promoGroup)
        }
      })
      cartItemGroups.forEach((group) => {
        groupQtyMap[group] = (groupQtyMap[group] || 0) + g.sumArray(cartItem.qtyList)
      })
    })

    // Step 3: Find matching promo rule (fromQty <= totalQty <= toQty) for each group
    let matchedPromoCode = null
    let matchedDiscountPercent = null
    itemGroups.forEach((group) => {
      const totalQty = groupQtyMap[group] || 0
      const matchedRule = promoRules.find(
        (rule) => rule.promoGroup === group && totalQty >= rule.fromQty && totalQty <= rule.toQty
      )
      if (matchedRule) {
        matchedPromoCode = matchedRule.promoCode
        matchedDiscountPercent = matchedRule.discountPercent
        /*
      console.log(
        `Matched promo group ${group} with total qty ${totalQty}: promo code ${matchedPromoCode}, discount ${matchedDiscountPercent}%`
      )
      */
      }
    })

    // Step 4: Apply discount if promoCode found and discountPercent > 0
    if (matchedPromoCode && matchedDiscountPercent > 0) {
      item.discountPrice = g.decimalNumber(item.price1 * (1 - matchedDiscountPercent / 100), 2)
      item.price5Org = item.price5
      item.price5 = item.discountPrice
      item.promoCode = matchedPromoCode
      item.isDiscountPriceApplied = true
      item.promoDiscountPercent = matchedDiscountPercent
      // console.log(
      //   `Applied promo code ${matchedPromoCode} with discount ${matchedDiscountPercent}% to item ${item.style}`
      // )
    } else {
      item.price5 = item.price5Org || item.price5
      item.isDiscountPriceApplied = false
      item.promoDiscountPercent = null
    }
  }
  // >> method
  const initCart = () => {
    isLoading.value = false
    cartData.value = null
    //addToCartData.value = []
    cartItemCount.value = 0
  }
  const addToCart = async (cartItemList) => {
    let isSuccessful = false
    progressbarStore.showLoading()
    try {
      const response = await $axios.post(useApi().cartApi.CART_ADD, cartItemList)
      if (response.header.isSuccessful) {
        refreshCartItemCount()
        //console.log('add to cart response data', response.data)
        //if (!response.data) showSnackbar(new Error(`The style '${productNo}' could not be found.`))
        isSuccessful = true
      }
    } catch (e) {
      showSnackbar(e)
    }
    setTimeout(() => {
      progressbarStore.hideLoading()
    }, 100)
    return isSuccessful
  }
  const _getCartItemGrandTotal = () =>
    cartData.value?.cartItemList?.reduce((total, item) => {
      return total + g.sumArray(item.qtyList)
    }, 0)

  const getCart = async (withLoading = true) => {
    progressbarStore.showLoading()
    if (withLoading) isLoading.value = true
    try {
      const response = await $axios.get(useApi().cartApi.CART)
      if (response.header.isSuccessful) {
        cartData.value = response.data
        cartItemCount.value = _getCartItemGrandTotal()
      }
    } catch (e) {
      showSnackbar(e)
    }
    if (withLoading) isLoading.value = false
    setTimeout(() => {
      progressbarStore.hideLoading()
    }, 100)
  }
  const updateCartItem = async (cartItem) => {
    progressbarStore.showLoading()
    //isLoading.value = true
    let isSuccessful = false
    try {
      const param = {
        cartItemNo: cartItem.cartItemNo,
        style: cartItem.style,
        color: cartItem.color,
        po: cartItem.po,
        qtys: cartItem.qtyList
      }
      const response = await $axios.put(useApi().cartApi.CART, param)
      if (response.header.isSuccessful) {
        await getCart(false)
        isSuccessful = true
      }
    } catch (e) {
      showSnackbar(e)
    }
    //isLoading.value = false
    setTimeout(() => {
      progressbarStore.hideLoading()
    }, 100)
    return isSuccessful
  }
  const deleteCartItem = async (cartItem) => {
    progressbarStore.showLoading()
    //isLoading.value = true
    try {
      const param = {
        cartItemNo: cartItem.cartItemNo,
        style: cartItem.style,
        color: cartItem.color
      }
      const response = await $axios.delete(useApi().cartApi.CART, { data: param })
      if (response.header.isSuccessful) {
        await getCart(false)
      }
    } catch (e) {
      showSnackbar(e)
    }
    //isLoading.value = false
    setTimeout(() => {
      progressbarStore.hideLoading()
    }, 100)
  }
  const refreshCartItemCount = async () => {
    try {
      const response = await $axios.get(useApi().cartApi.CART_COUNT)
      if (response.header.isSuccessful) {
        cartItemCount.value = response.data
      }
    } catch (e) {
      showSnackbar(e)
    }
  }
  const getOrderModelForConfirmPopup = (orderModel, minValue = appConfig.web.confirmPopupThresholdQty) => {
    //console.log('>getOrderModelForConfirmPopup', orderModel)
    if (minValue == 0) return []
    return Object.entries(orderModel)
      .filter(([_, value]) => value >= minValue)
      .map(([key, value]) => {
        const parts = key.split('|')
        if (parts.length !== 3) return null

        const [styleNo, colorCode, sizeName] = parts
        return {
          styleNo: styleNo,
          colorCode: colorCode,
          sizeName: sizeName,
          qty: value
        }
      })
      .filter(Boolean)
  }
  return {
    initCart,
    addToCart,
    getCart,
    updateCartItem,
    deleteCartItem,
    refreshCartItemCount,
    getOrderModelForConfirmPopup,
    isLoading,
    cartData,
    //addToCartData,
    cartItemCount,
    cartItemTotal,
    cartItemListWithTotals,
    offPriceStatus
  }
})
